import { refreshAdmin } from './admin-auth-api';
import { readErrorMessage } from './http-json';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';

export interface AdminAccountConsoleConsumerUser {
  id: string;
  status: string;
  displayName: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminAccountConsoleIdentity {
  id: string;
  provider: string;
  identityType: string;
  identityValue: string;
  isVerified: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminAccountConsoleSubscriptionAccount {
  id: string;
  consumerUserId: string;
  username: string;
  status: string;
  displayName: string | null;
  subscriptionLabel: string | null;
  maxActiveWechatConnections: number;
  createdByAdminUserId: string | null;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
}

export interface AdminAccountConsoleAccessRecord {
  id: string;
  agentId: string;
  source: string;
  status: string;
  isDefault: boolean;
  expiresAt: string | null;
  subscriptionPlan: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminAccountConsolePayment {
  id: string;
  source: string;
  status: string;
  amountMinor: number;
  currency: string;
  merchantOrderNo: string | null;
  wechatTransactionId: string | null;
  provider: string | null;
  providerSessionId: string | null;
  providerReference: string | null;
  businessReference: string | null;
  description: string | null;
  createdAt: string;
  paidAt: string | null;
}

export interface AdminAccountConsoleFulfillmentRecord {
  id: string;
  paymentIntentId: string;
  paymentAttemptId: string | null;
  targetType: string;
  targetReference: string;
  mode: string;
  status: string;
  retryCount: number;
  lastErrorCode: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface AdminAccountConsoleChatMessage {
  id: string;
  threadId: string;
  source: string;
  agentProfile: string | null;
  role: string;
  content: string;
  createdAt: string;
}

export interface AdminAccountConsoleItem {
  consumerUser: AdminAccountConsoleConsumerUser;
  identities: AdminAccountConsoleIdentity[];
  phoneMatches: string[];
  subscriptionAccounts: AdminAccountConsoleSubscriptionAccount[];
  accessRecords: AdminAccountConsoleAccessRecord[];
  payments: AdminAccountConsolePayment[];
  fulfillmentRecords: AdminAccountConsoleFulfillmentRecord[];
  recentMessages: AdminAccountConsoleChatMessage[];
}

export interface AdminAccountConsoleListResponse {
  items: AdminAccountConsoleItem[];
  total: number;
}

interface ApiConsumerUser {
  id: string;
  status: string;
  display_name: string | null;
  first_seen_at: string;
  last_seen_at: string;
  created_at: string;
  updated_at: string;
}

interface ApiIdentity {
  id: string;
  provider: string;
  identity_type: string;
  identity_value: string;
  is_verified: boolean;
  first_seen_at: string;
  last_seen_at: string;
  created_at: string;
  updated_at: string;
}

interface ApiSubscriptionAccount {
  id: string;
  consumer_user_id: string;
  username: string;
  status: string;
  display_name: string | null;
  subscription_label: string | null;
  max_active_wechat_connections: number;
  created_by_admin_user_id: string | null;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
}

interface ApiAccessRecord {
  id: string;
  agent_id: string;
  source: string;
  status: string;
  is_default: boolean;
  expires_at: string | null;
  subscription_plan: string | null;
  created_at: string;
  updated_at: string;
}

interface ApiPayment {
  id: string;
  source: string;
  status: string;
  amount_minor: number;
  currency: string;
  merchant_order_no: string | null;
  wechat_transaction_id: string | null;
  provider: string | null;
  provider_session_id: string | null;
  provider_reference: string | null;
  business_reference: string | null;
  description: string | null;
  created_at: string;
  paid_at: string | null;
}

interface ApiFulfillmentRecord {
  id: string;
  payment_intent_id: string;
  payment_attempt_id: string | null;
  target_type: string;
  target_reference: string;
  mode: string;
  status: string;
  retry_count: number;
  last_error_code: string | null;
  created_at: string;
  completed_at: string | null;
}

interface ApiChatMessage {
  id: string;
  thread_id: string;
  source: string;
  agent_profile: string | null;
  role: string;
  content: string;
  created_at: string;
}

interface ApiItem {
  consumer_user: ApiConsumerUser;
  identities: ApiIdentity[];
  phone_matches: string[];
  subscription_accounts: ApiSubscriptionAccount[];
  access_records: ApiAccessRecord[];
  payments: ApiPayment[];
  fulfillment_records: ApiFulfillmentRecord[];
  recent_messages: ApiChatMessage[];
}

interface ApiListResponse {
  items: ApiItem[];
  total: number;
}

let adminSessionRefreshPromise: Promise<string | null> | null = null;

async function refreshAdminAccessToken(): Promise<string | null> {
  if (adminSessionRefreshPromise) {
    return adminSessionRefreshPromise;
  }

  adminSessionRefreshPromise = refreshAdmin()
    .then((session) => {
      useAdminAuthStore.getState().setSession(session.accessToken, session.user);
      return session.accessToken;
    })
    .catch(() => {
      useAdminAuthStore.getState().clearSession();
      return null;
    })
    .finally(() => {
      adminSessionRefreshPromise = null;
    });

  return adminSessionRefreshPromise;
}

async function requestAdminAccountConsole<T>(
  accessToken: string,
  input: RequestInfo | URL,
  init: RequestInit,
  actionLabel: string,
): Promise<T> {
  const send = (token: string) =>
    fetch(input, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });

  let response = await send(accessToken);
  if (response.status === 401) {
    const refreshedAccessToken = await refreshAdminAccessToken();
    if (refreshedAccessToken) {
      response = await send(refreshedAccessToken);
    }
  }

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(`${actionLabel} failed (${response.status}): ${message}`);
  }

  return response.json() as Promise<T>;
}

function normalizeConsumerUser(payload: ApiConsumerUser): AdminAccountConsoleConsumerUser {
  return {
    id: payload.id,
    status: payload.status,
    displayName: payload.display_name,
    firstSeenAt: payload.first_seen_at,
    lastSeenAt: payload.last_seen_at,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
}

function normalizeIdentity(payload: ApiIdentity): AdminAccountConsoleIdentity {
  return {
    id: payload.id,
    provider: payload.provider,
    identityType: payload.identity_type,
    identityValue: payload.identity_value,
    isVerified: payload.is_verified,
    firstSeenAt: payload.first_seen_at,
    lastSeenAt: payload.last_seen_at,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
}

function normalizeSubscriptionAccount(
  payload: ApiSubscriptionAccount,
): AdminAccountConsoleSubscriptionAccount {
  return {
    id: payload.id,
    consumerUserId: payload.consumer_user_id,
    username: payload.username,
    status: payload.status,
    displayName: payload.display_name,
    subscriptionLabel: payload.subscription_label,
    maxActiveWechatConnections: payload.max_active_wechat_connections,
    createdByAdminUserId: payload.created_by_admin_user_id,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
    lastLoginAt: payload.last_login_at,
  };
}

function normalizeAccessRecord(payload: ApiAccessRecord): AdminAccountConsoleAccessRecord {
  return {
    id: payload.id,
    agentId: payload.agent_id,
    source: payload.source,
    status: payload.status,
    isDefault: payload.is_default,
    expiresAt: payload.expires_at,
    subscriptionPlan: payload.subscription_plan,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
}

function normalizePayment(payload: ApiPayment): AdminAccountConsolePayment {
  return {
    id: payload.id,
    source: payload.source,
    status: payload.status,
    amountMinor: payload.amount_minor,
    currency: payload.currency,
    merchantOrderNo: payload.merchant_order_no,
    wechatTransactionId: payload.wechat_transaction_id,
    provider: payload.provider,
    providerSessionId: payload.provider_session_id,
    providerReference: payload.provider_reference,
    businessReference: payload.business_reference,
    description: payload.description,
    createdAt: payload.created_at,
    paidAt: payload.paid_at,
  };
}

function normalizeFulfillmentRecord(
  payload: ApiFulfillmentRecord,
): AdminAccountConsoleFulfillmentRecord {
  return {
    id: payload.id,
    paymentIntentId: payload.payment_intent_id,
    paymentAttemptId: payload.payment_attempt_id,
    targetType: payload.target_type,
    targetReference: payload.target_reference,
    mode: payload.mode,
    status: payload.status,
    retryCount: payload.retry_count,
    lastErrorCode: payload.last_error_code,
    createdAt: payload.created_at,
    completedAt: payload.completed_at,
  };
}

function normalizeChatMessage(payload: ApiChatMessage): AdminAccountConsoleChatMessage {
  return {
    id: payload.id,
    threadId: payload.thread_id,
    source: payload.source,
    agentProfile: payload.agent_profile,
    role: payload.role,
    content: payload.content,
    createdAt: payload.created_at,
  };
}

function normalizeItem(payload: ApiItem): AdminAccountConsoleItem {
  return {
    consumerUser: normalizeConsumerUser(payload.consumer_user),
    identities: payload.identities.map(normalizeIdentity),
    phoneMatches: payload.phone_matches,
    subscriptionAccounts: payload.subscription_accounts.map(normalizeSubscriptionAccount),
    accessRecords: payload.access_records.map(normalizeAccessRecord),
    payments: payload.payments.map(normalizePayment),
    fulfillmentRecords: payload.fulfillment_records.map(normalizeFulfillmentRecord),
    recentMessages: payload.recent_messages.map(normalizeChatMessage),
  };
}

export async function searchAdminAccountConsole(
  accessToken: string,
  options: { q?: string; limit?: number } = {},
): Promise<AdminAccountConsoleListResponse> {
  const params = new URLSearchParams();
  if (options.q?.trim()) {
    params.set('q', options.q.trim());
  }
  if (options.limit != null) {
    params.set('limit', String(options.limit));
  }
  const path = params.size
    ? `/api/v1/admin/account-console?${params.toString()}`
    : '/api/v1/admin/account-console';
  const payload = await requestAdminAccountConsole<ApiListResponse>(
    accessToken,
    path,
    { method: 'GET' },
    'Search accounts',
  );
  return {
    items: payload.items.map(normalizeItem),
    total: payload.total,
  };
}

export async function disableAdminAccountMembership(
  accessToken: string,
  accountId: string,
): Promise<AdminAccountConsoleItem> {
  const payload = await requestAdminAccountConsole<ApiItem>(
    accessToken,
    `/api/v1/admin/account-console/subscription-accounts/${accountId}/disable`,
    { method: 'POST' },
    'Disable membership',
  );
  return normalizeItem(payload);
}

export async function restoreAdminAccountMembership(
  accessToken: string,
  accountId: string,
): Promise<AdminAccountConsoleItem> {
  const payload = await requestAdminAccountConsole<ApiItem>(
    accessToken,
    `/api/v1/admin/account-console/subscription-accounts/${accountId}/restore`,
    { method: 'POST' },
    'Restore membership',
  );
  return normalizeItem(payload);
}
